import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { AppRoutes } from '@/router/routes.types'
import { RouteBuilders } from '@/router/navigation.types'
import { selectOrganization } from '@/store/selectors/authSelectors'

interface AppNavigationResult {
    organization: { uuid: string; name: string } | null
    navigateToRoute: (route: AppRoutes) => void
    navigateToPath: (path: string) => void
    buildAppUrl: (path: string) => string
    // Route builders for type-safe dynamic routes
    routes: typeof RouteBuilders
    // Convenience methods for common navigation
    navigateToDashboard: () => void
    navigateToProfile: () => void
    navigateToTeam: () => void
    navigateToUpload: () => void
}

/**
 * Hook for application navigation without organization-scoped URLs
 * Uses organization context from Redux store instead of URL parameters
 */
export const useAppNavigation = (): AppNavigationResult => {
    const navigate = useNavigate()
    const organization = useSelector(selectOrganization)

    /**
     * Navigate to a specific route using AppRoutes enum
     * @param route - Route from AppRoutes enum
     */
    const navigateToRoute = (route: AppRoutes) => {
        navigate(route)
    }

    /**
     * Navigate to a custom path - simplified pass-through
     * Trade-off: Less type safety but working navigation for notification URLs
     * @param path - Path to navigate to
     */
    const navigateToPath = (path: string) => {
        if (!path) return
        const normalized = path.startsWith('/') ? path : `/${path}`

        navigate(normalized)
    }

    /**
     * Build a full URL (no longer needs organization scope)
     * @param path - Path to build URL for
     * @returns Full path
     */
    const buildAppUrl = (path: string): string => {
        return path
    }

    // Convenience methods for common navigation
    const navigateToDashboard = () => navigateToRoute(AppRoutes.DASHBOARD)
    const navigateToProfile = () => navigateToRoute(AppRoutes.PROFILE)
    const navigateToTeam = () => navigateToRoute(AppRoutes.TEAM)
    const navigateToUpload = () => navigateToRoute(AppRoutes.UPLOAD)

    return {
        organization,
        navigateToRoute,
        navigateToPath,
        buildAppUrl,
        routes: RouteBuilders,
        navigateToDashboard,
        navigateToProfile,
        navigateToTeam,
        navigateToUpload,
    }
}
